import { useIntl } from 'react-intl';

import {
  downloadAttachment,
  downloadDocumentAttachment,
  uploadAttachments,
} from '~/api/document';
import { EM_DASH } from '~/common/constants/unicode';
import { Box } from '~/eds/src/components/Box/Box';
import { FileInput as EdsFileInput } from '~/eds/src/components/FileInput/FileInput';
import { Filename } from '~/eds/src/components/Filename/Filename';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { downloadFile } from '~/utils/files';

import { BaseFieldProps } from './fieldTypes';

interface FileData {
  value: string | number;
  label?: string;
  display_value?: string;
}

interface AttachmentFieldProps extends BaseFieldProps {
  documentId: number;
  isDownloadDocumentAttachmentEnabled: boolean;
  onFileAttachError: () => void;
}

export const AttachmentField = ({
  field,
  editMode,
  editData,
  documentId,
  isDownloadDocumentAttachmentEnabled,
  onEditDataChange,
  onFileAttachError,
}: AttachmentFieldProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const downloadFileAttachment = (fileObject: {
    value: string | number;
    display_value: string;
  }) => {
    const downloadFn = isDownloadDocumentAttachmentEnabled
      ? () => downloadDocumentAttachment(documentId, fileObject.value)
      : () => downloadAttachment(fileObject.value);

    downloadFn()
      .then((file) => {
        toast({
          message: intl.formatMessage(
            {
              id: 'DocumentOverviewPanel.downloadSuccess',
              defaultMessage: '{fileName} successfully downloaded.',
              description:
                'Success message when file download completes. Example: fileName = "contract.pdf"',
            },
            { fileName: fileObject.display_value },
          ),
          status: 'success',
        });
        downloadFile(file, fileObject.display_value);
      })
      .catch(() =>
        toast({
          message: intl.formatMessage({
            id: 'DocumentOverviewPanel.downloadError',
            defaultMessage: 'Download attachment failed. Please try again.',
            description: 'Error message when file download fails',
          }),
          status: 'danger',
        }),
      );
  };

  const handleRemoveFile = (fileValue: string | number) => {
    const currentValues = editData[field.id];
    if (Array.isArray(currentValues)) {
      const updatedValues = currentValues.filter(
        (v: FileData) => v.value !== fileValue,
      );
      onEditDataChange(field.id, updatedValues);
    }
  };

  const handleFileDataChangeHelper = (
    newFiles: Array<{
      id: string | number;
      fileName: string;
      fileType: string;
    }>,
  ) => {
    if (!newFiles || !newFiles.length) {
      toast({
        message: intl.formatMessage({
          id: 'DocumentOverviewPanel.couldNotAttachFile',
          defaultMessage: 'Could not attach file.',
          description: 'Error message when file attachment fails',
        }),
        status: 'danger',
      });
      return;
    }

    const oldItems = editData[field.id];
    const oldItemsArray = Array.isArray(oldItems) ? oldItems : [];
    const newItems = newFiles.map((fileData) => ({
      value: fileData.id,
      label: fileData.fileName + fileData.fileType,
    }));

    onEditDataChange(field.id, [...oldItemsArray, ...newItems]);

    toast({
      message: intl.formatMessage({
        id: 'DocumentOverviewPanel.attachFileSuccess',
        defaultMessage: 'Attach file succeeded.',
        description: 'Success message when file attachment succeeds',
      }),
      status: 'success',
    });
  };

  if (editMode) {
    const presentValues = editData[field.id] || [];
    const hasFiles = Array.isArray(presentValues) && presentValues.length > 0;

    return (
      <Box
        flex="1"
        style={{ display: 'block' }}
        data-testid={`doc-overview-panel-${field.name.replace(/\s/g, '-')}`}
      >
        <EdsFileInput
          name={field.name}
          value={[]}
          isMulti
          width="100%"
          disabled={!field.is_editable}
          onChange={(files) =>
            uploadAttachments(
              files,
              handleFileDataChangeHelper,
              onFileAttachError,
            )
          }
        />
        {hasFiles && (
          <Layout mt={2}>
            <Layout as="ul" direction="column" spacing={1} m={0} p={0}>
              {presentValues.map((fileData: FileData) => (
                <Layout
                  as="li"
                  key={fileData.value}
                  direction="row"
                  align="center"
                  spacing={1}
                  maxW="100%"
                >
                  <Layout flex="1 1 0" minW={0} overflowX="hidden">
                    <Filename name={fileData.label || ''} />
                  </Layout>
                  <Layout flex="0 0 auto">
                    <IconButton
                      icon="x"
                      size="xs"
                      disabled={!field.is_editable}
                      onClick={() => handleRemoveFile(fileData.value)}
                      tooltip={intl.formatMessage(
                        {
                          id: 'AttachmentField.removeFile',
                          defaultMessage: 'Remove {fileName}',
                          description:
                            'Tooltip for removing a file attachment. Example: fileName = "contract.pdf"',
                        },
                        {
                          fileName: fileData.label || '',
                        },
                      )}
                    />
                  </Layout>
                </Layout>
              ))}
            </Layout>
          </Layout>
        )}
      </Box>
    );
  }

  if (Array.isArray(field.value) && field.value.length) {
    return (
      <Layout as="ul" direction="column" spacing={1} m={0} p={0}>
        {field.value.map((fileObject: FileData) => (
          <Layout as="li" key={fileObject.value}>
            <Filename
              name={fileObject.display_value || ''}
              onClick={() => {
                const displayValue = fileObject.display_value;
                if (displayValue) {
                  downloadFileAttachment({
                    value: fileObject.value,
                    display_value: displayValue,
                  });
                }
              }}
            />
          </Layout>
        ))}
      </Layout>
    );
  }

  return EM_DASH;
};
